// ================= CUSTOM DECK STORAGE =================
//
// Saves player-built decks, and merges the valid ones into the game so the setup screen can offer
// them beside the fifteen built-ins.
//
// STORAGE CAN ALWAYS FAIL, and it fails in more ways than "the browser has no localStorage": private
// windows throw on write, quota is finite, and some embedded webviews expose the API and then refuse
// it. Every access is wrapped the way js/sfx.js does it, and when storage is unavailable the store
// says so through `available` rather than throwing into whatever called it. The builder reads that
// and shows a designed fallback -- what it must never do is present a Save button that silently
// does nothing.
//
// THE REVALIDATION RULE IS THE POINT OF THIS FILE, and it protects playtesters specifically. A
// stored deck is a list of card IDS, and the card pool moves: 17 of 208 commits have touched
// js/cards*.js. Nothing in the engine guards an unknown id -- makeFighter maps the array straight
// through, so a deck holding a card that no longer exists becomes a fighter holding `undefined`,
// and the failure surfaces somewhere deep in resolution with nothing on screen to explain it.
//
// So every read revalidates. A deck that no longer passes is NOT merged into the game, NOT deleted,
// and NOT silently repaired: it is kept exactly as saved and flagged for repair, because the player
// built it and the game losing cards is not their mistake to absorb. Deleting it would be the game
// destroying player data to tidy up after its own content change.
(function () {
  'use strict';

  const KEY = 'sc.customDecks.v1';
  // WHY A DELETE LEAVES SOMETHING BEHIND. Once decks sync between devices, removing the row is not
  // enough to express "this deck is gone": a device that has been offline comes back holding the
  // deck, sees the server does not have it, and helpfully uploads it again. The player deletes a
  // deck and it returns, which reads as the game inventing decks. So a delete records WHEN, and
  // js/deck-sync.js turns that into a delete the server can be told about.
  //
  // Separate key rather than a field on the record, so list(), inspect() and mergeIntoGame() keep
  // seeing exactly what they saw before and none of their existing behaviour has to be re-proved.
  const TOMBSTONE_KEY = 'sc.customDecks.tombstones.v1';
  // Tombstones are tiny and only useful until every device has heard. Capped so that a player who
  // churns through hundreds of decks cannot slowly fill their own storage quota with the record of
  // decks that no longer exist.
  const MAX_TOMBSTONES = 200;
  const SCHEMA_VERSION = 1;
  const FORMAT = 'custom';

  const R = () => window.DeckRules;

  // ---------------- raw storage, all of it fallible ----------------

  function storage() {
    try {
      const s = window.localStorage;
      if (!s) return null;
      // Presence is not permission. Some webviews expose localStorage and throw on first write, so
      // the probe is a real round trip rather than a truthiness check.
      const probe = '__sc_probe__';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (e) { return null; }
  }

  function available() { return storage() !== null; }

  function readAll() {
    const s = storage();
    if (!s) return {};
    try {
      const raw = s.getItem(KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
    } catch (e) {
      // Corrupt JSON is treated as no decks rather than as a crash. It is unrecoverable either way,
      // and taking the boot down over it would make one bad string cost the player the whole game.
      return {};
    }
  }

  function writeAll(map) {
    const s = storage();
    if (!s) return false;
    try {
      s.setItem(KEY, JSON.stringify(map));
      return true;
    } catch (e) { return false; }
  }

  // ---------------- ids ----------------
  //
  // Random rather than sequential, and checked against DECK_META, because a custom deck's id shares
  // a namespace with the built-ins the moment mergeIntoGame runs -- and a collision would have a
  // player's deck silently replace a shipped one.
  function newId(taken) {
    for (let attempt = 0; attempt < 50; attempt++) {
      let s = '';
      const g = window.crypto && window.crypto.getRandomValues;
      if (g) {
        const buf = new Uint32Array(2);
        window.crypto.getRandomValues(buf);
        s = (buf[0].toString(36) + buf[1].toString(36)).slice(0, 10);
      } else {
        s = Math.random().toString(36).slice(2, 12);
      }
      while (s.length < 10) s += '0';
      const id = 'custom_' + s;
      if (!window.DECK_META[id] && !(taken && taken[id])) return id;
    }
    return 'custom_' + Date.now().toString(36);
  }

  // ---------------- validation on the way in and on the way out ----------------

  // THE STORE DOES NOT ASK WHO OWNS WHAT, and asking it with nothing to answer from was a bug that
  // shipped with Season 1. DeckRules.validate reads a missing owned set as "owns nothing" -- right
  // for a deck from a stranger, and wrong here, where every deck arrives by one of two routes: the
  // builder, which validated it live against the server's answer and will not enable Save while a
  // card is UNOWNED, or a sync of the player's own account. So validating here with no owned set
  // refused Save on every deck holding a card the player had BOUGHT, and parked every such deck a
  // sync pulled down as "needs repair" at boot -- where index.html never learns ownership at all,
  // because js/main.js renders Setup from storage and never awaits the network.
  //
  // So the store's legality is the deck's SHAPE: size, tiers, copies, Styles, cards that exist.
  // UNOWNED is the one issue it drops, and only that one -- a store card is still a card, and every
  // other rule still reads it. Found by Signature Series 01 Phase B, whose Save a copy is this same
  // save() and would have refused every upgraded deck the progression loop exists to produce.
  function storageIssues(cards, style) {
    return R().validate(cards, style, FORMAT).filter(i => i.code !== 'UNOWNED');
  }

  // What is wrong with a stored record, as a list of issues. Structure first, then the deck rules:
  // a record whose `cards` is not an array is a different problem from one whose cards no longer
  // exist, and the repair UI needs to tell them apart.
  function inspect(record) {
    const issues = [];
    if (!record || typeof record !== 'object') {
      return { issues: [{ code: 'BAD_RECORD', message: 'This saved deck is not readable.' }], missing: [] };
    }
    if (record.schemaVersion !== SCHEMA_VERSION) {
      issues.push({ code: 'BAD_SCHEMA', message: 'Saved by a different version of the game.' });
    }
    if (!Array.isArray(record.cards)) {
      issues.push({ code: 'BAD_RECORD', message: 'This saved deck has no card list.' });
      return { issues: issues, missing: [] };
    }
    const missing = record.cards.filter(id => !window.CARDS[id]);
    if (missing.length) {
      const n = missing.length;
      issues.push({
        code: 'MISSING_CARDS',
        message: 'needs repair (' + n + ' card' + (n === 1 ? '' : 's') + ' no longer exist' +
                 (n === 1 ? 's' : '') + ')'
      });
    }
    // The deck rules run on the FULL list, including ids that no longer resolve -- DeckRules
    // reports those as UNKNOWN_CARD, which is the same fact from the rules' side and keeps the two
    // from disagreeing about whether a deck is legal.
    storageIssues(record.cards, record.style).forEach(i => issues.push(i));
    return { issues: issues, missing: missing };
  }

  // Every stored deck, each with its verdict attached. This is what the builder's My Decks list
  // renders from: decks that fail are still LISTED, so a player can find and repair them.
  function list() {
    const map = readAll();
    return Object.keys(map).map(id => {
      const record = map[id];
      const verdict = inspect(record);
      return {
        id: id,
        record: record,
        issues: verdict.issues,
        missing: verdict.missing,
        playable: verdict.issues.length === 0
      };
    }).sort((a, b) => String((b.record || {}).updatedAt || '').localeCompare(String((a.record || {}).updatedAt || '')));
  }

  function get(id) {
    const map = readAll();
    return map[id] || null;
  }

  // ---------------- saving ----------------
  //
  // Returns { ok, id } or { ok: false, error, issues } -- never throws, because the caller is a
  // button handler and a thrown save leaves the page looking like it worked.
  function save(draft) {
    if (!available()) {
      return { ok: false, error: 'UNAVAILABLE',
               message: 'This browser will not store decks. Copy your share code to keep this one.' };
    }
    const issues = storageIssues(draft.cards, draft.style);
    if (issues.length) {
      return { ok: false, error: 'ILLEGAL', issues: issues,
               message: 'This deck is not legal yet.' };
    }
    const map = readAll();
    const isNew = !draft.id || !map[draft.id];
    if (isNew && Object.keys(map).length >= R().MAX_SAVED) {
      return { ok: false, error: 'FULL',
               message: 'You have ' + R().MAX_SAVED + ' saved decks, which is the limit. ' +
                        'Delete one to save another.' };
    }
    const now = new Date().toISOString();
    const id = draft.id && map[draft.id] ? draft.id : newId(map);
    map[id] = {
      schemaVersion: SCHEMA_VERSION,
      id: id,
      name: String(draft.name || '').slice(0, R().NAME_MAX) || 'Untitled deck',
      style: draft.style,
      artKey: draft.artKey,
      description: String(draft.description || '').slice(0, R().DESC_MAX),
      cards: draft.cards.slice(),
      createdAt: (map[id] && map[id].createdAt) || now,
      updatedAt: now
    };
    if (!writeAll(map)) {
      return { ok: false, error: 'WRITE_FAILED',
               message: 'Saving failed — this browser may be out of storage space.' };
    }
    return { ok: true, id: id, record: map[id] };
  }

  // ---------------- tombstones ----------------

  function readTombstones() {
    const s = storage();
    if (!s) return {};
    try {
      const raw = s.getItem(TOMBSTONE_KEY);
      const p = raw ? JSON.parse(raw) : null;
      return (p && typeof p === 'object' && !Array.isArray(p)) ? p : {};
    } catch (e) { return {}; }
  }

  function writeTombstones(map) {
    const s = storage();
    if (!s) return false;
    // Oldest first out, by the timestamp they carry, so the cap drops the ones every device has had
    // the longest to hear about rather than whichever the key order happened to put last.
    const ids = Object.keys(map);
    if (ids.length > MAX_TOMBSTONES) {
      ids.sort((a, b) => String(map[a]).localeCompare(String(map[b])));
      ids.slice(0, ids.length - MAX_TOMBSTONES).forEach(id => { delete map[id]; });
    }
    try { s.setItem(TOMBSTONE_KEY, JSON.stringify(map)); return true; }
    catch (e) { return false; }
  }

  function tombstones() { return readTombstones(); }

  // Called by the sync once the server has been told, so a delete is announced once rather than on
  // every sync forever.
  function clearTombstone(id) {
    const map = readTombstones();
    if (!(id in map)) return false;
    delete map[id];
    return writeTombstones(map);
  }

  function remove(id) {
    const map = readAll();
    if (!map[id]) return false;
    delete map[id];
    const wrote = writeAll(map);
    // The tombstone is recorded even when the deck was never synced. It costs one small string, and
    // the alternative is deck-store having to know whether sync is switched on -- which would make
    // deleting a deck behave differently depending on whether the player has an account.
    if (wrote) {
      const t = readTombstones();
      t[id] = new Date().toISOString();
      writeTombstones(t);
    }
    return wrote;
  }

  // ---------------- the setup hook ----------------
  //
  // Called ONCE at boot, before anything reads the deck list. Only decks that pass inspect() are
  // merged; the rest stay in storage, listed for repair, and unreachable from the setup screen --
  // which is the difference between a deck a player can fix and a deck that crashes their bout.
  //
  // Returns a summary rather than nothing, so the boot path and the test can both see what
  // happened without re-reading storage.
  // CALLED AGAIN AFTER A SYNC, which is why it now PRUNES as well as merges. At boot it runs once
  // against an empty game and the two halves are indistinguishable; the moment a sync can delete a
  // deck that another device removed, a merge that only ever adds leaves that deck in the setup
  // screen's dropdown forever -- selectable, playable, and gone from storage. Pruning only touches
  // entries this function put there: `category === 'custom'` is the marker, so a shipped deck can
  // never be removed by it however the storage looks.
  function mergeIntoGame() {
    const out = { merged: [], needsRepair: [], removed: [], available: available() };
    const stillHere = {};
    list().forEach(e => { if (e.playable) stillHere[e.id] = 1; });
    Object.keys(window.DECK_META).forEach(id => {
      if (window.DECK_META[id].category !== 'custom' || stillHere[id]) return;
      delete window.DECK_META[id];
      delete window.DECKS[id];
      out.removed.push(id);
    });
    list().forEach(entry => {
      if (!entry.playable) { out.needsRepair.push(entry.id); return; }
      const r = entry.record;
      const art = (window.FIGHTER_ART && window.FIGHTER_ART[r.artKey]) || {};
      // .slice() is belt on top of braces and CANNOT be tested: storage is a JSON string, so
      // readAll() re-parses into fresh arrays on every call and nothing downstream can reach the
      // stored record whether this copies or not. A `merge-shares-the-array` mutant survives here
      // by construction — recorded so the next person does not spend an afternoon on it. It stays
      // because it states the intent, and because an in-memory cache added later would need it.
      window.DECKS[r.id] = r.cards.slice();
      window.DECK_META[r.id] = {
        name: r.name,
        style: r.style,
        family: window.FAMILY_OF_STYLE[r.style],
        category: 'custom',
        // A deck with no description still has to say something on the setup screen: the blurb
        // element is always rendered, and an empty one reads as a broken row rather than as a deck
        // whose owner did not write a line.
        archetype: r.description || ('Custom ' + (window.STYLE_LABELS[r.style] || r.style) + ' deck'),
        // FROM THE REGISTRY, not built by string concatenation. `'assets/portraits/' + artKey +
        // '.png'` assumed every art key names a file at a fixed path with a fixed extension, which
        // was true only while art keys were deck ids. js/fighter-art.js owns the paths now, so art
        // can be a .webp, live in a subdirectory, or be shared between two keys without this line
        // knowing. An artKey the registry has never heard of yields null rather than a 404 path.
        portrait: art.portrait || null,
        rangeFigure: art.rangeFigure || null,
        // THE KEY ITSELF, not only the two paths it resolves to. Move art is looked up per
        // (artKey, pose) rather than as a single stored path, so a consumer that has the deck needs
        // the key back. The shipped decks get away without this because their ids ARE art keys; a
        // custom deck's id is `custom_...` and is in no registry, so dropping this line is the
        // difference between a custom deck's cards showing its fighter performing every move and
        // showing generic drawings — with nothing anywhere reporting a problem.
        artKey: r.artKey || null
      };
      out.merged.push(r.id);
    });
    return out;
  }

  window.DeckStore = {
    KEY: KEY,
    TOMBSTONE_KEY: TOMBSTONE_KEY,
    SCHEMA_VERSION: SCHEMA_VERSION,
    available: available,
    list: list,
    get: get,
    save: save,
    remove: remove,
    inspect: inspect,
    mergeIntoGame: mergeIntoGame,
    tombstones: tombstones,
    clearTombstone: clearTombstone,
    // Exported for js/deck-sync.js, which has to mint an id for a deck arriving from another device
    // and must use the SAME collision check as a locally created one -- a custom deck's id shares a
    // namespace with the built-ins the moment mergeIntoGame runs.
    newLocalId: newId,
    // Exported so a test can drive the corrupt/absent cases without reaching into localStorage.
    _readAll: readAll,
    _writeAll: writeAll
  };
})();
